import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { ArrowUpRight, Check, Clock, Shield, Sparkles, ChevronDown, ChevronUp, Settings, GitBranch, Layers, Globe, DollarSign, Zap, Play } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: GitBranch, title: 'Drag-and-drop builder', text: 'Create custom IVR flows visually without coding. Connect call paths, set up menu options, and configure routing rules.' },
  { icon: Layers, title: 'Pre-built templates', text: 'Get started faster with templates for sales routing, support departments, after-hours handling, and more.' },
  { icon: Zap, title: 'Dynamic menus', text: 'Update IVR menus on the fly. Add seasonal prompts, temporary redirects, or emergency announcements.' },
  { icon: Globe, title: 'Smart routing', text: 'Route calls based on time of day, caller location, or customer segment to reach the right team instantly.' },
];

const useCases = [
  { title: 'Sales & support split', text: 'Route calls to sales or support teams based on customer selection or business hours.' },
  { title: 'Department routing', text: 'Direct callers to billing, technical support, or general inquiries with simple menu options.' },
  { title: 'After-hours handling', text: 'Automated menu for non-business hours with voicemail, emergency contacts, or callback scheduling.' },
  { title: 'Multi-language support', text: 'Offer language selection before connecting to agents for international customer service.' },
];

const templates = [
  { name: 'Sales & Support Split', description: 'Route calls to sales or support based on customer selection', icon: GitBranch },
  { name: 'Department Routing', description: 'Direct callers to billing, technical support, or general inquiries', icon: Layers },
  { name: 'After-Hours Handler', description: 'Automated menu for non-business hours with voicemail option', icon: Clock },
  { name: 'Multi-language Support', description: 'Offer language selection before connecting to agents', icon: Globe },
];

const benefits = [
  { icon: Clock, title: 'Reduce wait times', description: 'Intelligent routing gets customers to the right agent faster, improving satisfaction.' },
  { icon: DollarSign, title: 'No extra cost', description: 'IVR menus are included free with Zuri Voice - no setup or per-call fees.' },
  { icon: Shield, title: '24/7 availability', description: 'Handle calls around the clock with automated menus and routing, even when agents are offline.' },
  { icon: Sparkles, title: 'Easy updates', description: 'Change menus instantly without downtime or technical expertise.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is an IVR menu?',
    answer: 'IVR (Interactive Voice Response) is an automated phone system that interacts with callers through voice or keypad inputs. It presents menu options like "Press 1 for sales, press 2 for support" and routes calls accordingly.'
  },
  {
    question: 'Do I need coding skills to create IVR menus?',
    answer: 'No coding required. Our drag-and-drop IVR builder lets you create complex call flows visually. Simply drag nodes, connect them, and configure options through an intuitive interface.'
  },
  {
    question: 'Are IVR menus included in the pricing?',
    answer: 'Yes! IVR menus are completely free with Zuri Voice. There are no setup fees, monthly charges, or per-call costs for using IVR functionality. You only pay for standard voice minutes.'
  },
  {
    question: 'What templates are available?',
    answer: 'We offer pre-built templates for common scenarios: sales & support split, department routing, after-hours handling, multi-language support, emergency announcements, and more. Each template can be customized to fit your needs.'
  },
  {
    question: 'Can I update IVR menus dynamically?',
    answer: 'Absolutely. You can update IVR menus in real-time without any downtime. Add seasonal greetings, change routing rules, or update department information instantly from the dashboard.'
  },
  {
    question: 'How does smart routing work?',
    answer: 'Smart routing uses conditions like time of day, caller location, customer segment, or previous interactions to route calls intelligently. For example, route international calls to specialized agents or direct VIP customers to priority queues.'
  },
  {
    question: 'Can I integrate IVR with other Zuri Voice features?',
    answer: 'Yes, IVR integrates seamlessly with text-to-speech, call recording, transcription, and conference calling. You can create sophisticated flows that use all these features together.'
  },
  {
    question: 'What happens when agents are unavailable?',
    answer: 'Configure fallback options like voicemail, callback scheduling, or emergency contacts. IVR can automatically detect agent availability and route calls accordingly or inform callers of expected wait times.'
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function IvrMenus() {
  return (
    <>
      <SEO
        title="IVR Menus - Dynamic Call Routing | Zuri Voice"
        description="Dynamically build your IVRs to help answer and filter calls without the need for a human agent. Reduce wait times and improve efficiency. Included free with Zuri Voice."
        path="/ivr-menus"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Settings className="h-4 w-4" /> Included free
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                IVR menus that <span className="gradient-text">route smarter</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Dynamically build your IVRs to help answer and filter calls without the need for a human agent. Reduce wait times and improve efficiency. No extra cost.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/zuri-voice"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Back to Zuri Voice <Settings className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative grid gap-4">
                {[
                  { icon: GitBranch, label: 'Drag-and-Drop Builder', color: 'from-blue-500 to-blue-600' },
                  { icon: Layers, label: 'Pre-built Templates', color: 'from-purple-500 to-purple-600' },
                  { icon: Zap, label: 'Dynamic Menus', color: 'from-green-500 to-green-600' },
                  { icon: Globe, label: 'Smart Routing', color: 'from-orange-500 to-orange-600' },
                ].map((item, i) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 backdrop-blur"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${item.color} text-white shadow-lg`}>
                      <item.icon className="h-6 w-6" />
                    </div>
                    <span className="font-semibold text-white">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful IVR features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to automate call routing.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="templates-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="templates-heading" className="section-title">Ready-to-use templates</h2>
            <p className="section-subtitle mx-auto mt-4">Get started fast with pre-built IVR templates.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {templates.map((template) => (
              <div key={template.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <template.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-slate-900">{template.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{template.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why use IVR menus?</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform your call handling.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Build intelligent call flows in minutes with our visual IVR builder and pre-built templates.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Settings,
                    title: 'Choose a template',
                    description: 'Start with a pre-built template or create a custom IVR from scratch using our drag-and-drop builder.',
                  },
                  {
                    icon: GitBranch,
                    title: 'Design your flow',
                    description: 'Add menu options, set up routing rules, and configure conditions like time of day or caller location.',
                  },
                  {
                    icon: Play,
                    title: 'Test and deploy',
                    description: 'Preview your IVR flow, make adjustments, and deploy it instantly. Update anytime without downtime.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Routing capabilities</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Time-based routing (business hours, after-hours)</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Location-based routing (country, region)</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Customer segment routing (VIP, new, returning)</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Skill-based routing (language, expertise)</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Load balancing across agents</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Fallback to voicemail or callback</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about IVR menus</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}